import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addContactGroup,
  addContactGroupUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ApiException, ContactGroup } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import axios from 'axios';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

interface AddContactGroupProps {
  open: boolean;
  handleClose: () => void;
  contactGroupList: ContactGroup[];
  userEmail: string;
  userView?: boolean;
  changeLogRoles: string[];
}

export default function AddContactGroupDialog({
  open,
  handleClose,
  contactGroupList,
  userEmail,
  userView,
  changeLogRoles,
}: AddContactGroupProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [groupName, setGroupName] = useState('');
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => addContactGroup(groupName, userEmail, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'New label added successfully!',
      });

      contactGroupList.push(data);
      queryClient.setQueryData([UserKeys.Labels, userEmail], contactGroupList);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        if (errorResponse.message?.includes('409')) {
          setHasError(true);
        } else {
          handleErrors({
            error,
            snackbarMessage:
              'There was an issue adding this label. Please refresh and try again.',
          });
        }
      }
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .create_contact_label,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () => addContactGroupUserPortal(groupName),
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'New label added successfully!',
        });

        contactGroupList.push(data);
        queryClient.setQueryData(
          [UserKeys.Labels, userEmail],
          contactGroupList
        );
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        if (axios.isAxiosError(error)) {
          const errorResponse = error.response?.data as ApiException;
          if (errorResponse.message?.includes('409')) {
            setHasError(true);
          } else {
            handleErrors({
              error,
              snackbarMessage:
                'There was an issue adding this label. Please refresh and try again.',
            });
          }
        }
      },
    });

  useEffect(() => {
    if (!open) {
      setGroupName('');
    }
    if (hasError && !open) {
      setHasError(false);
    }
  }, [open, hasError]);

  function handleSubmit() {
    if (
      !userView &&
      isAbleToSeeUIElement(changeLogRoles, UserRoles.AddContactGroups.value)
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      userPortalMutate();
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Create Label"
      dialogContent={
        <Box>
          <TextField
            autoFocus
            id="contactGroup"
            label="Label Name"
            placeholder="e.g. VIP Clients"
            fullWidth
            autoComplete="off"
            error={hasError}
            helperText={hasError ? 'Label already exists.' : ''}
            value={groupName}
            onChange={(event) => setGroupName(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Add Label',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: groupName.length === 0,
      }}
    />
  );
}
